import { useEffect } from 'react';
import { useUser } from '../../auth/AuthProvider';
import { isManager, latestArmpByJob } from '../../data/domain';
import { useJobRecords, useJobs } from '../../data/hooks';
import { dateTime, nowIso } from '../../lib/format';
import { putEntity } from '../../sync/outbox';
import { Modal } from '../../ui/Modal';
import { useUI } from '../../ui/UIProvider';

export function ArmpReviewModal({ jobKey }: { jobKey: string }) {
  const user = useUser();
  const ui = useUI();
  const records = useJobRecords();
  const jobs = useJobs();
  const record = latestArmpByJob(records).get(jobKey) ?? null;
  const job = jobs.find((item) => item.id === (record?.jobId || jobKey)) ?? null;
  const canReview = isManager(user);

  function openArmp() {
    ui.close('armpReview');
    if (!job) return;
    ui.open('jobEditor', { mode: 'edit', jobId: job.id });
    ui.open('compliance', { jobId: job.id, type: 'armp' });
  }

  // No ARMP yet for this high-risk job: go straight to creating one.
  useEffect(() => { if (records.length && !record && job) openArmp(); }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!record) return null;
  const review = record.review;

  async function confirm() {
    if (!record || !canReview) return;
    await putEntity('job-records', { ...record, review: { status: 'confirmed', reviewedBy: user.displayName, reviewerAccountId: user.id, reviewerRole: user.role, reviewedAt: nowIso() } }, `Reviewed ARMP for ${record.clientName}`);
    ui.close('armpReview');
  }

  return (
    <Modal id="armpReviewModal" size="sm" onClose={() => ui.close('armpReview')} labelledBy="armpReviewTitle">
      <header className="modal-header" id="armpReviewDetails">
        <p className="eyebrow">High-risk ARMP review</p>
        <h2 id="armpReviewTitle">Confirm ARMP</h2>
        <p className="form-help"><b>{record.clientName}</b> · {record.jobService || 'Scheduled service'} · {dateTime(record.createdAt) || 'Date not recorded'}</p>
      </header>
      {review?.status === 'confirmed' ? <p className="notice notice-success">Confirmed by {review.reviewedBy} on {dateTime(review.reviewedAt)}.</p> : <p className="notice notice-warning">This ARMP revision is awaiting review.</p>}
      {!canReview && <p className="form-error" id="armpReviewPermission">Only operations and administrator accounts can confirm an ARMP review.</p>}
      <div className="form-actions">
        {review?.status !== 'confirmed' && <button className="btn btn-primary" id="confirmArmpReview" disabled={!canReview} onClick={confirm}>Confirm review</button>}
        <button className="btn btn-secondary" onClick={() => { ui.close('armpReview'); ui.open('recordDetails', { recordId: record.id }); }}>View ARMP</button>
        {job && <button className="btn btn-secondary" id="openArmpForReview" onClick={openArmp}>Update ARMP</button>}
      </div>
    </Modal>
  );
}
